import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Plus, X, Send } from "lucide-react";
import { cn } from "@/lib/cn";
import { useLanguageStore } from "@/stores/languageStore";
import type { ScheduledPost } from "@/stores/channelStore";

const SPRING = { type: "spring" as const, stiffness: 300, damping: 30 };

const SECTION_VARIANTS = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: SPRING },
};

const COLLAPSE_VARIANTS = {
  hidden: { opacity: 0, height: 0, overflow: "hidden" as const },
  visible: {
    opacity: 1,
    height: "auto",
    overflow: "visible" as const,
    transition: { ...SPRING, opacity: { duration: 0.2 } },
  },
  exit: {
    opacity: 0,
    height: 0,
    overflow: "hidden" as const,
    transition: { duration: 0.2 },
  },
};

function StatusBadge({ status }: { status: ScheduledPost["status"] }) {
  const styles: Record<ScheduledPost["status"], string> = {
    pending: "bg-amber-50 text-amber-700",
    published: "bg-emerald-50 text-emerald-700",
    failed: "bg-red-50 text-red-700",
    cancelled: "bg-gray-50 text-gray-700",
  };

  return (
    <span
      className={cn(
        "rounded-[var(--radius-full)] px-1.5 py-0.5 text-[10px] font-medium",
        styles[status],
      )}
    >
      {status}
    </span>
  );
}

function ScheduledPostItem({
  post,
  onCancel,
}: {
  post: ScheduledPost;
  onCancel: () => void;
}) {
  const displayDate = new Date(post.scheduledAt).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-[var(--radius-md)] p-3",
        "bg-[var(--color-bg)]",
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="truncate text-sm text-[var(--color-text)]">
          {post.content}
        </p>
        <div className="flex items-center gap-2">
          <Clock className="h-3 w-3 text-[var(--color-text-muted)]" />
          <span className="text-xs text-[var(--color-text-muted)]">
            {displayDate}
          </span>
          <StatusBadge status={post.status} />
        </div>
      </div>
      <button
        type="button"
        onClick={onCancel}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full
                   text-red-600 active:bg-red-500/10"
        aria-label={`Cancel scheduled post: ${post.content.slice(0, 30)}`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function PostSchedulerSection({
  channelId,
  posts,
  onSchedule,
  onCancel,
}: {
  channelId: string;
  posts: ScheduledPost[];
  onSchedule: (channelId: string, content: string, scheduledAt: string) => Promise<void>;
  onCancel: (postId: string) => void;
}) {
  const language = useLanguageStore((state) => state.language);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [content, setContent] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");

  async function handleSchedule() {
    if (!content.trim() || !scheduledAt) return;
    await onSchedule(channelId, content.trim(), scheduledAt);
    setContent("");
    setScheduledAt("");
    setIsFormOpen(false);
  }

  return (
    <motion.section variants={SECTION_VARIANTS} className="mb-5">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
        Post Scheduler
      </h2>

      <div className="rounded-[var(--radius-lg)] bg-[var(--color-surface)] shadow-sm p-4">
        {!isFormOpen && (
          <button
            type="button"
            onClick={() => setIsFormOpen(true)}
            className={cn(
              "flex h-11 w-full items-center justify-center gap-2 rounded-[var(--radius-md)]",
              "bg-[var(--color-primary)] text-sm font-medium text-white",
              "transition-colors active:scale-[0.97]",
            )}
            aria-label="Schedule a new post"
          >
            <Plus className="h-4 w-4" />
            Schedule a Post
          </button>
        )}

        <AnimatePresence>
          {isFormOpen && (
            <motion.div
              key="scheduler-form"
              variants={COLLAPSE_VARIANTS}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-[var(--color-text)]">
                  New Scheduled Post
                </h3>
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-full
                             text-[var(--color-text-muted)] active:bg-[var(--color-border-subtle)]"
                  aria-label="Close scheduler form"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your post content..."
                rows={4}
                aria-label="Post content"
                className={cn(
                  "mb-3 w-full resize-none rounded-[var(--radius-md)]",
                  "shadow-sm bg-[var(--color-bg)] p-3",
                  "text-sm text-[var(--color-text)]",
                  "placeholder:text-[var(--color-text-muted)]",
                  "outline-none focus:ring-2 focus:ring-[var(--color-primary)]",
                )}
              />

              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                aria-label="Schedule date and time"
                className={cn(
                  "mb-3 w-full rounded-[var(--radius-md)]",
                  "shadow-sm bg-[var(--color-bg)] px-3 py-2.5",
                  "text-sm text-[var(--color-text)]",
                  "outline-none focus:ring-2 focus:ring-[var(--color-primary)]",
                )}
              />

              <button
                type="button"
                onClick={handleSchedule}
                disabled={!content.trim() || !scheduledAt}
                className={cn(
                  "flex h-11 w-full items-center justify-center gap-2 rounded-[var(--radius-md)]",
                  "text-sm font-medium text-white transition-colors active:scale-[0.97]",
                  content.trim() && scheduledAt
                    ? "bg-[var(--color-primary)]"
                    : "cursor-not-allowed bg-[var(--color-border)]",
                )}
                aria-label="Confirm schedule post"
              >
                <Send className="h-4 w-4" />
                {language === "am" ? "የሙከራ ፖስት አስቀምጥ" : "Save Preview Post"}
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {posts.length > 0 && (
          <div className="mt-4 flex flex-col gap-2">
            <p className="text-xs font-medium text-[var(--color-text-muted)]">
              Upcoming ({posts.length})
            </p>
            {posts.map((post) => (
              <ScheduledPostItem
                key={post.id}
                post={post}
                onCancel={() => onCancel(post.id)}
              />
            ))}
          </div>
        )}
      </div>
    </motion.section>
  );
}
